import React, { useState, useEffect } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import type { EntitlementConfiguration, IntOrUnlimited, IntOrUnlimitedWithCountDisabledUnits } from "client/api-v2";
import { ErrorAlert } from "components/alert/ErrorAlert";

type EditEntitlementOverridesDialogProps = {
  open: boolean;
  subscriptionId: string;
  subscriptionName: string;
  currentOverrides?: EntitlementConfiguration;
  planInclusions?: EntitlementConfiguration;
  handleClose: () => void;
  submitOverrides: (overrides: EntitlementConfiguration) => Promise<void>;
  isCloudPlatform?: boolean;
  applicableEntitlementKeys?: string[];
};

type BooleanOverrideStatus = {
  label: string;
  color: "default" | "primary" | "warning";
  variant: "outlined" | "filled";
};

function getBooleanOverrideStatus(value: boolean | null, planDefault: boolean | null): BooleanOverrideStatus {
  if (value === null) {
    if (planDefault === null) return { label: "Not set", color: "default", variant: "outlined" };
    return { label: `Plan default: ${planDefault ? "On" : "Off"}`, color: "default", variant: "outlined" };
  }
  if (planDefault !== null && value === planDefault) {
    return { label: "Override matches plan", color: "warning", variant: "outlined" };
  }
  return { label: "Overridden", color: "primary", variant: "filled" };
}

type BooleanFeatureKey = keyof Pick<
  EntitlementConfiguration,
  | "telemetryEnforced"
  | "serviceNow"
  | "spaceLevelInsights"
  | "auditLogFeeds"
  | "jiraServiceManagement"
  | "priorityTask"
  | "globalDeploymentFreeze"
  | "processTemplates"
  | "policies"
  | "projectTemplates"
  | "scim"
  | "restrictedPermissions"
>;

type BooleanOverrideRowProps = {
  label: string;
  value: boolean | null;
  planDefault: boolean | null;
  disabled: boolean;
  onChange: (value: boolean | null) => void;
};

function BooleanOverrideRow({ label, value, planDefault, disabled, onChange }: BooleanOverrideRowProps) {
  const effective = value ?? planDefault ?? false;
  const status = getBooleanOverrideStatus(value, planDefault);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <FormControlLabel
        control={<Switch checked={effective} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />}
        label={label}
      />
      <Chip
        size="small"
        label={status.label}
        color={status.color}
        variant={status.variant}
        onDelete={value !== null ? () => onChange(null) : undefined}
        deleteIcon={
          value !== null ? (
            // Why: MUI's SvgIcon defaults to aria-hidden, which hides the reset from screen readers and role queries.
            <CancelIcon role="button" aria-hidden={false} aria-label={`Reset ${label} to plan default`} />
          ) : undefined
        }
      />
    </Box>
  );
}

export function EditEntitlementOverridesDialog({
  isCloudPlatform,
  applicableEntitlementKeys,
  ...props
}: EditEntitlementOverridesDialogProps) {
  const isApplicable = (key: string) => !applicableEntitlementKeys || applicableEntitlementKeys.includes(key);
  // Why: Targets and Machines are one entitlement, so show only one: Targets if already overridden, else Machines.
  const usesTargets = !!props.currentOverrides?.targets;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown | undefined>();

  const [projects, setProjects] = useState<string>("");
  const [tenants, setTenants] = useState<string>("");
  const [machines, setMachines] = useState<string>("");
  const [targets, setTargets] = useState<string>("");
  const [taskCap, setTaskCap] = useState<string>("");
  const [storageLimitGb, setStorageLimitGb] = useState<string>("");
  const [nodes, setNodes] = useState<string>("");
  const [retentionDays, setRetentionDays] = useState<string>("");
  const [users, setUsers] = useState<string>("");
  const [spaces, setSpaces] = useState<string>("");
  const [connectionAgents, setConnectionAgents] = useState<string>("");
  const [usageDeclarationExpiryDays, setUsageDeclarationExpiryDays] = useState<string>("");

  const [telemetryEnforced, setTelemetryEnforced] = useState<boolean | null>(null);
  const [serviceNow, setServiceNow] = useState<boolean | null>(null);
  const [spaceLevelInsights, setSpaceLevelInsights] = useState<boolean | null>(null);
  const [auditLogFeeds, setAuditLogFeeds] = useState<boolean | null>(null);
  const [jiraServiceManagement, setJiraServiceManagement] = useState<boolean | null>(null);
  const [priorityTask, setPriorityTask] = useState<boolean | null>(null);
  const [globalDeploymentFreeze, setGlobalDeploymentFreeze] = useState<boolean | null>(null);
  const [processTemplates, setProcessTemplates] = useState<boolean | null>(null);
  const [policies, setPolicies] = useState<boolean | null>(null);
  const [projectTemplates, setProjectTemplates] = useState<boolean | null>(null);
  const [scim, setScim] = useState<boolean | null>(null);
  const [restrictedPermissions, setRestrictedPermissions] = useState<boolean | null>(null);
  const [excessAllowedWithUsageDeclaration, setExcessAllowedWithUsageDeclaration] = useState<boolean | null>(null);

  useEffect(() => {
    if (props.open && props.currentOverrides) {
      setProjects(getValueString(props.currentOverrides.projects));
      setTenants(getValueString(props.currentOverrides.tenants));
      setMachines(getValueString(props.currentOverrides.machines));
      setTargets(getValueString(props.currentOverrides.targets));
      setTaskCap(getValueString(props.currentOverrides.taskCap));
      setStorageLimitGb(props.currentOverrides.storageLimitGb?.toString() || "");
      setNodes(getValueString(props.currentOverrides.nodes));
      setRetentionDays(getValueString(props.currentOverrides.retentionDays));
      setUsers(getValueString(props.currentOverrides.users));
      setSpaces(getValueString(props.currentOverrides.spaces));
      setConnectionAgents(props.currentOverrides.connectionAgents?.toString() || "");
      setUsageDeclarationExpiryDays(props.currentOverrides.usageDeclarationExpiryDays?.toString() || "");

      setTelemetryEnforced(props.currentOverrides.telemetryEnforced ?? null);
      setServiceNow(props.currentOverrides.serviceNow ?? null);
      setSpaceLevelInsights(props.currentOverrides.spaceLevelInsights ?? null);
      setAuditLogFeeds(props.currentOverrides.auditLogFeeds ?? null);
      setJiraServiceManagement(props.currentOverrides.jiraServiceManagement ?? null);
      setPriorityTask(props.currentOverrides.priorityTask ?? null);
      setGlobalDeploymentFreeze(props.currentOverrides.globalDeploymentFreeze ?? null);
      setProcessTemplates(props.currentOverrides.processTemplates ?? null);
      setPolicies(props.currentOverrides.policies ?? null);
      setProjectTemplates(props.currentOverrides.projectTemplates ?? null);
      setScim(props.currentOverrides.scim ?? null);
      setRestrictedPermissions(props.currentOverrides.restrictedPermissions ?? null);
      setExcessAllowedWithUsageDeclaration(props.currentOverrides.excessAllowedWithUsageDeclaration ?? null);
    }
  }, [props.open, props.currentOverrides]);

  const getValueString = (value: IntOrUnlimitedWithCountDisabledUnits | IntOrUnlimited | null | undefined): string => {
    if (!value) return "";
    if (value.isUnlimited) return "unlimited";
    if (value.limit !== null && value.limit !== undefined) return value.limit.toString();
    return "";
  };

  const parseValueWithCountDisabled = (value: string): IntOrUnlimitedWithCountDisabledUnits | null => {
    if (value.trim() === "") return null;
    if (value.toLowerCase() === "unlimited") {
      return { isUnlimited: true, limit: null, countDisabledUnits: false };
    }
    const num = parseInt(value, 10);
    if (isNaN(num)) return null;
    return { isUnlimited: false, limit: num, countDisabledUnits: false };
  };

  const parseValue = (value: string): IntOrUnlimited | null => {
    if (value.trim() === "") return null;
    if (value.toLowerCase() === "unlimited") {
      return { isUnlimited: true, limit: null };
    }
    const num = parseInt(value, 10);
    if (isNaN(num)) return null;
    return { isUnlimited: false, limit: num };
  };

  const parseSimpleIntValue = (value: string): number | null => {
    if (value.trim() === "") return null;
    const num = parseInt(value, 10);
    if (isNaN(num)) return null;
    return num;
  };

  const isValidIntOrUnlimited = (value: string) => {
    if (value.trim() === "") return true;
    if (value.toLowerCase() === "unlimited") return true;
    const num = parseInt(value, 10);
    return !isNaN(num) && value.trim() === num.toString();
  };

  const isValidInt = (value: string) => {
    if (value.trim() === "") return true;
    const num = parseInt(value, 10);
    return !isNaN(num) && value.trim() === num.toString();
  };

  const numericFields: { label: string; key: string; value: string; validate: (v: string) => boolean }[] = [
    { label: "Projects", key: "projects", value: projects, validate: isValidIntOrUnlimited },
    { label: "Tenants", key: "tenants", value: tenants, validate: isValidIntOrUnlimited },
    usesTargets
      ? { label: "Targets", key: "targets", value: targets, validate: isValidIntOrUnlimited }
      : { label: "Machines", key: "machines", value: machines, validate: isValidIntOrUnlimited },
    { label: "Nodes", key: "nodes", value: nodes, validate: isValidIntOrUnlimited },
    { label: "Retention Days", key: "retentionDays", value: retentionDays, validate: isValidIntOrUnlimited },
    { label: "Users", key: "users", value: users, validate: isValidIntOrUnlimited },
    { label: "Spaces", key: "spaces", value: spaces, validate: isValidIntOrUnlimited },
    { label: "Connection Agents", key: "connectionAgents", value: connectionAgents, validate: isValidInt },
    ...(isCloudPlatform
      ? []
      : [{ label: "Task Cap", key: "taskCap", value: taskCap, validate: isValidIntOrUnlimited }]),
    { label: "Storage Limit (GB)", key: "storageLimitGb", value: storageLimitGb, validate: isValidInt },
    {
      label: "Usage Declaration Expiry Days",
      key: "usageDeclarationExpiryDays",
      value: usageDeclarationExpiryDays,
      validate: isValidInt,
    },
  ].filter((f) => isApplicable(f.key));

  type BooleanFeatureField = {
    label: string;
    key: BooleanFeatureKey;
    value: boolean | null;
    set: (v: boolean | null) => void;
  };

  const booleanFeatureFields: BooleanFeatureField[] = (
    [
      { label: "Telemetry Enforced", key: "telemetryEnforced", value: telemetryEnforced, set: setTelemetryEnforced },
      { label: "ServiceNow", key: "serviceNow", value: serviceNow, set: setServiceNow },
      {
        label: "Space Level Insights",
        key: "spaceLevelInsights",
        value: spaceLevelInsights,
        set: setSpaceLevelInsights,
      },
      { label: "Audit Log Feeds", key: "auditLogFeeds", value: auditLogFeeds, set: setAuditLogFeeds },
      {
        label: "Jira Service Management",
        key: "jiraServiceManagement",
        value: jiraServiceManagement,
        set: setJiraServiceManagement,
      },
      { label: "Priority Task", key: "priorityTask", value: priorityTask, set: setPriorityTask },
      {
        label: "Global Deployment Freeze",
        key: "globalDeploymentFreeze",
        value: globalDeploymentFreeze,
        set: setGlobalDeploymentFreeze,
      },
      { label: "Process Templates", key: "processTemplates", value: processTemplates, set: setProcessTemplates },
      { label: "Policies", key: "policies", value: policies, set: setPolicies },
      { label: "Project Templates", key: "projectTemplates", value: projectTemplates, set: setProjectTemplates },
      { label: "SCIM", key: "scim", value: scim, set: setScim },
      {
        label: "Restricted Permissions",
        key: "restrictedPermissions",
        value: restrictedPermissions,
        set: setRestrictedPermissions,
      },
    ] satisfies BooleanFeatureField[]
  ).filter(({ key }) => isApplicable(key));

  const handleSubmit = async () => {
    const invalidFields = numericFields.filter((f) => !f.validate(f.value));
    if (invalidFields.length > 0) {
      const labels = invalidFields.map((f) => f.label).join(", ");
      setError(new Error(`Invalid value for: ${labels}. Enter a number, or "unlimited" where allowed.`));
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      const whenApplicable = <T,>(key: string, value: T) => (isApplicable(key) ? value : null);

      const overrides: EntitlementConfiguration = {
        projects: whenApplicable("projects", parseValueWithCountDisabled(projects)) ?? undefined,
        tenants: whenApplicable("tenants", parseValueWithCountDisabled(tenants)) ?? undefined,
        machines: usesTargets
          ? undefined
          : whenApplicable("machines", parseValueWithCountDisabled(machines)) ?? undefined,
        targets: usesTargets ? whenApplicable("targets", parseValue(targets)) ?? undefined : undefined,
        taskCap: whenApplicable("taskCap", parseValue(taskCap)),
        storageLimitGb: whenApplicable("storageLimitGb", parseSimpleIntValue(storageLimitGb)),
        nodes: whenApplicable("nodes", parseValue(nodes)) ?? undefined,
        retentionDays: whenApplicable("retentionDays", parseValue(retentionDays)) ?? undefined,
        users: whenApplicable("users", parseValue(users)) ?? undefined,
        spaces: whenApplicable("spaces", parseValue(spaces)) ?? undefined,
        connectionAgents: whenApplicable("connectionAgents", parseSimpleIntValue(connectionAgents)),
        telemetryEnforced: whenApplicable("telemetryEnforced", telemetryEnforced),
        serviceNow: whenApplicable("serviceNow", serviceNow),
        spaceLevelInsights: whenApplicable("spaceLevelInsights", spaceLevelInsights),
        auditLogFeeds: whenApplicable("auditLogFeeds", auditLogFeeds),
        jiraServiceManagement: whenApplicable("jiraServiceManagement", jiraServiceManagement),
        priorityTask: whenApplicable("priorityTask", priorityTask),
        globalDeploymentFreeze: whenApplicable("globalDeploymentFreeze", globalDeploymentFreeze),
        processTemplates: whenApplicable("processTemplates", processTemplates),
        policies: whenApplicable("policies", policies),
        projectTemplates: whenApplicable("projectTemplates", projectTemplates),
        scim: whenApplicable("scim", scim),
        restrictedPermissions: whenApplicable("restrictedPermissions", restrictedPermissions),
        usageDeclarationExpiryDays: whenApplicable(
          "usageDeclarationExpiryDays",
          parseSimpleIntValue(usageDeclarationExpiryDays)
        ),
        excessAllowedWithUsageDeclaration: whenApplicable(
          "excessAllowedWithUsageDeclaration",
          excessAllowedWithUsageDeclaration
        ),
      };

      await props.submitOverrides(overrides);
      handleCancel();
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setProjects("");
    setTenants("");
    setMachines("");
    setTargets("");
    setTaskCap("");
    setStorageLimitGb("");
    setNodes("");
    setRetentionDays("");
    setUsers("");
    setSpaces("");
    setConnectionAgents("");
    setUsageDeclarationExpiryDays("");
    setTelemetryEnforced(null);
    setServiceNow(null);
    setSpaceLevelInsights(null);
    setAuditLogFeeds(null);
    setJiraServiceManagement(null);
    setPriorityTask(null);
    setGlobalDeploymentFreeze(null);
    setProcessTemplates(null);
    setPolicies(null);
    setProjectTemplates(null);
    setScim(null);
    setRestrictedPermissions(null);
    setExcessAllowedWithUsageDeclaration(null);
    setError(undefined);
    props.handleClose();
  };

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        if (!loading) handleCancel();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Edit Entitlement Overrides
        <Typography
          variant="subtitle1"
          component="p"
          sx={{
            color: "text.secondary",
          }}
        >
          Subscription: {props.subscriptionName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Typography variant="h6">Capacity Limits</Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Enter a number or "unlimited" for numeric fields. Leave empty to remove override.
            </Typography>
          </Grid>

          {isApplicable("projects") && (
            <Grid size={6}>
              <TextField
                fullWidth
                label="Projects"
                value={projects}
                onChange={(e) => setProjects(e.target.value)}
                disabled={loading}
                helperText="Number or 'unlimited'"
              />
            </Grid>
          )}

          {isApplicable("tenants") && (
            <Grid size={6}>
              <TextField
                fullWidth
                label="Tenants"
                value={tenants}
                onChange={(e) => setTenants(e.target.value)}
                disabled={loading}
                helperText="Number or 'unlimited'"
              />
            </Grid>
          )}

          {isApplicable(usesTargets ? "targets" : "machines") && (
            <Grid size={6}>
              <TextField
                fullWidth
                label={usesTargets ? "Targets" : "Machines"}
                value={usesTargets ? targets : machines}
                onChange={(e) => (usesTargets ? setTargets : setMachines)(e.target.value)}
                disabled={loading}
                helperText="Number or 'unlimited'"
              />
            </Grid>
          )}

          {isApplicable("taskCap") && (
            <Grid size={6}>
              {isCloudPlatform ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Task cap for Cloud instances is managed via the <strong>Change task cap</strong> overflow action.
                </Typography>
              ) : (
                <TextField
                  fullWidth
                  label="Task Cap"
                  value={taskCap}
                  onChange={(e) => setTaskCap(e.target.value)}
                  disabled={loading}
                  helperText="Number or 'unlimited'"
                />
              )}
            </Grid>
          )}

          {isApplicable("storageLimitGb") && (
            <Grid size={6}>
              <TextField
                fullWidth
                label="Storage Limit (GB)"
                type="number"
                value={storageLimitGb}
                onChange={(e) => setStorageLimitGb(e.target.value)}
                disabled={loading}
              />
            </Grid>
          )}

          {isApplicable("nodes") && (
            <Grid size={6}>
              <TextField
                fullWidth
                label="Nodes"
                value={nodes}
                onChange={(e) => setNodes(e.target.value)}
                disabled={loading}
                helperText="Number or 'unlimited'"
              />
            </Grid>
          )}

          {isApplicable("retentionDays") && (
            <Grid size={6}>
              <TextField
                fullWidth
                label="Retention Days"
                value={retentionDays}
                onChange={(e) => setRetentionDays(e.target.value)}
                disabled={loading}
                helperText="Number or 'unlimited'"
              />
            </Grid>
          )}

          {isApplicable("users") && (
            <Grid size={6}>
              <TextField
                fullWidth
                label="Users"
                value={users}
                onChange={(e) => setUsers(e.target.value)}
                disabled={loading}
                helperText="Number or 'unlimited'"
              />
            </Grid>
          )}

          {isApplicable("spaces") && (
            <Grid size={6}>
              <TextField
                fullWidth
                label="Spaces"
                value={spaces}
                onChange={(e) => setSpaces(e.target.value)}
                disabled={loading}
                helperText="Number or 'unlimited'"
              />
            </Grid>
          )}

          {isApplicable("connectionAgents") && (
            <Grid size={6}>
              <TextField
                fullWidth
                label="Connection Agents"
                value={connectionAgents}
                onChange={(e) => setConnectionAgents(e.target.value)}
                disabled={loading}
                helperText="Number"
              />
            </Grid>
          )}

          {(isApplicable("usageDeclarationExpiryDays") || isApplicable("excessAllowedWithUsageDeclaration")) && (
            <Grid size={12}>
              <Typography variant="h6" sx={{ mt: 2 }}>
                Usage Declaration
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Expiry days requires customers to declare usage at least that often. Excess allowed lets them go over
                their limits while their declaration is current, and only takes effect when expiry days is set. Leave
                expiry days empty, or use the chip's × to remove the override.
              </Typography>
            </Grid>
          )}

          {isApplicable("usageDeclarationExpiryDays") && (
            <Grid size={6}>
              <TextField
                fullWidth
                label="Expiry Days"
                value={usageDeclarationExpiryDays}
                onChange={(e) => setUsageDeclarationExpiryDays(e.target.value)}
                disabled={loading}
                helperText="Number"
              />
            </Grid>
          )}

          {isApplicable("excessAllowedWithUsageDeclaration") && (
            <Grid size={6}>
              <BooleanOverrideRow
                label="Excess Allowed"
                value={excessAllowedWithUsageDeclaration}
                planDefault={props.planInclusions?.excessAllowedWithUsageDeclaration ?? null}
                disabled={loading}
                onChange={setExcessAllowedWithUsageDeclaration}
              />
            </Grid>
          )}

          <Grid size={12}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Features
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              The chip next to each switch shows whether the value comes from the plan or from an override. Use the
              chip's × to clear the override.
            </Typography>
          </Grid>

          {booleanFeatureFields.map(({ label, key, value, set }) => (
            <Grid key={key} size={6}>
              <BooleanOverrideRow
                label={label}
                value={value}
                planDefault={props.planInclusions?.[key] ?? null}
                disabled={loading}
                onChange={set}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      {error ? (
        <Box sx={{ px: 3, pt: 2 }}>
          <ErrorAlert error={error} />
        </Box>
      ) : null}
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
          {loading ? "Saving..." : "Save Overrides"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
